/**
 * Supabase Client Configuration
 * 
 * This is the typed Supabase client for the Novel Writing Assistant.
 * It provides type-safe access to the database and authentication.
 * 
 * Usage:
 * import { supabase } from '@/lib/supabase'
 * 
 * const { data, error } = await supabase
 *   .from('profiles')
 *   .select('*')
 *   .eq('id', userId)
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/types/supabase';
import { RECOVERY_STORAGE_KEY } from '@/stores/modules/auth/constants';

const persistRecoveryFlag = (value: boolean): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (value) {
    sessionStorage.setItem(RECOVERY_STORAGE_KEY, '1');
  } else {
    sessionStorage.removeItem(RECOVERY_STORAGE_KEY);
  }
};

const primeRecoveryFlagFromHash = (): void => {
  if (typeof window === 'undefined' || !window.location.hash) {
    return;
  }

  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  if (hashParams.get('type') === 'recovery') {
    persistRecoveryFlag(true);
  }
};

primeRecoveryFlagFromHash();

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl) {
  throw new Error(
    'Missing VITE_SUPABASE_URL environment variable. ' +
    'Please check your .env.local file.'
  );
}

if (!supabaseAnonKey) {
  throw new Error(
    'Missing VITE_SUPABASE_ANON_KEY environment variable. ' +
    'Please check your .env.local file.'
  );
}

// Create typed Supabase client
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    debug: false
  }
});

supabase.auth.onAuthStateChange((event) => {
  if (event === 'PASSWORD_RECOVERY') {
    persistRecoveryFlag(true);
  } else if (event === 'SIGNED_OUT' || event === 'SIGNED_IN') {
    persistRecoveryFlag(false);
  }
});

// Export types for convenience
export type { Database } from '@/types/supabase';
export type Tables<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Row'];
export type Inserts<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Insert'];
export type Updates<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Update'];

// Export specific table types for convenience
export type Profile = Tables<'profiles'>;
export type Subscription = Tables<'subscriptions'>;
export type SubscriptionPlan = Tables<'subscription_plans'>;
export type UsagePeriod = Tables<'usage_periods'>;
export type ApiUsageLog = Tables<'api_usage_logs'>;
export type Manuscript = Tables<'manuscripts'>;
export type Chapter = Tables<'chapters'>;
export type GenerationJob = Tables<'generation_jobs'>;
export type WebhookEvent = Tables<'webhook_events'>;

